import React from 'react'
import { getAuthHeaders } from '../utils/authHeaders'
import {
  Modal,
  Button,
  Select,
  Space,
  Checkbox,
  Alert,
  Typography,
  Divider,
  message,
  Spin,
  Card,
  theme,
} from 'antd'
import { MinusCircleOutlined, PlusOutlined, SwapOutlined } from '@ant-design/icons'
import axios from 'axios'
import {
  CharacterRelationshipRow,
  createEmptyRelationshipRow,
  parseRelationshipText,
  rowsToRequestPayload,
  RELATIONSHIP_GENDERS,
  RELATIONSHIP_FORMALITIES,
  isNonStoryRelationship,
  isCharacterPlaceholderValue,
  LegacyRelationshipFormatError,
} from '../utils/characterRelationships'
import './RelationshipMatrixEditorModal.css'

interface ChapterOption {
  chapterGuid: string
  chapterName: string
  chapterNumber: number
}

interface RelationshipMatrixEditorModalProps {
  visible: boolean
  onClose: () => void
  onOpenAnalysis: () => void
  analyzeDisabled?: boolean
  initialChapterGuid?: string | null
  bookId: string
  userId: string
  apiBase: string
  chapters: ChapterOption[]
  onSaved?: () => Promise<void> | void
}

interface CachedChapterData {
  rows: CharacterRelationshipRow[]
  isNonStory: boolean
  lastUpdatedAt?: string | null
  rawText?: string | null
}

type RelationshipRowSort = 'none' | 'characterA' | 'characterB'

const formatRelationshipGenderLabel = (gender: string) => {
  if (gender === 'male') return 'Masculine'
  if (gender === 'female') return 'Feminine'
  return 'Not specified'
}

const SELECTABLE_RELATIONSHIP_GENDERS = RELATIONSHIP_GENDERS.filter(
  (gender) => gender !== 'unknown'
)

const RelationshipMatrixEditorModal: React.FC<RelationshipMatrixEditorModalProps> = ({
  visible,
  onClose,
  onOpenAnalysis,
  analyzeDisabled = false,
  initialChapterGuid = null,
  bookId,
  userId,
  apiBase,
  chapters,
  onSaved,
}) => {
  const { token } = theme.useToken()
  const [selectedChapterGuid, setSelectedChapterGuid] = React.useState<string | null>(null)
  const [rows, setRows] = React.useState<CharacterRelationshipRow[]>([])
  const [isNonStory, setIsNonStory] = React.useState(false)
  const [lastUpdatedAt, setLastUpdatedAt] = React.useState<string | null>(null)
  const [loading, setLoading] = React.useState(false)
  const [loadError, setLoadError] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [rowSort, setRowSort] = React.useState<RelationshipRowSort>('none')
  const [characterLookup, setCharacterLookup] = React.useState<Record<string, string>>({})
  const [nameToIdLookup, setNameToIdLookup] = React.useState<Record<string, string>>({})
  const [characterOptions, setCharacterOptions] = React.useState<
    Array<{ label: string; value: string; keywords: string }>
  >([])
  const cacheRef = React.useRef<Record<string, CachedChapterData>>({})

  const GRID_TEMPLATE = '1.9fr 1.6fr 1.9fr 1.6fr 1.25fr 1.25fr 104px'

  const orderedChapters = React.useMemo(() => {
    const sorter = [...chapters]
    sorter.sort((a, b) => (a.chapterNumber || 0) - (b.chapterNumber || 0))
    return sorter
  }, [chapters])

  const loadCharacterLookup = React.useCallback(async () => {
    if (!userId) return
    try {
      const { data } = await axios.get(
        `${apiBase}/uploadedbooks/book/${bookId}/characters/registry`,
        {
          params: { userId },
          headers: await getAuthHeaders(),
        }
      )
      const lookup: Record<string, string> = {}
      const reverse: Record<string, string> = {}
      const characters = data?.characters || []
      characters.forEach((character: any) => {
        const id = character?.id
        const name = String(character?.canonicalName || '').trim()
        if (!id || !name || isCharacterPlaceholderValue(name)) {
          return
        }
        if (id && name) {
          lookup[id] = name
          reverse[name.toLowerCase()] = id
        }
        const aliases = character?.aliases || []
        aliases.forEach((alias: string) => {
          const trimmed = alias?.trim()
          if (!trimmed || isCharacterPlaceholderValue(trimmed)) return
          reverse[trimmed.toLowerCase()] = id
        })
      })
      const options = characters
        .map((character: any) => {
          const id = character?.id
          const name = String(character?.canonicalName || '').trim()
          if (!id || !name || isCharacterPlaceholderValue(name)) return null
          const aliases = (character?.aliases || []).filter((alias: string) => {
            const trimmed = alias?.trim()
            return Boolean(trimmed) && !isCharacterPlaceholderValue(trimmed)
          })
          const keywords = [name, ...aliases].join(' ').toLowerCase()
          return { label: name, value: id, keywords }
        })
        .filter(Boolean)
        .sort((a: any, b: any) =>
          (a?.label || '').toLowerCase().localeCompare((b?.label || '').toLowerCase())
        )
      setCharacterLookup(lookup)
      setNameToIdLookup(reverse)
      setCharacterOptions(options)
    } catch (err) {
      console.error('Failed to load character registry for relationship editor', err)
    }
  }, [apiBase, bookId, userId])

  const resetState = React.useCallback(() => {
    setLoadError(false)
    setRows([])
    setIsNonStory(false)
    setLastUpdatedAt(null)
  }, [])

  React.useEffect(() => {
    if (!visible) {
      return
    }
    loadCharacterLookup()
  }, [visible, loadCharacterLookup])

  const handleClose = React.useCallback(() => {
    resetState()
    onClose()
  }, [onClose, resetState])

  const loadChapter = React.useCallback(
    async (chapterGuid: string) => {
      if (!chapterGuid) return
      setLoadError(false)
      setLoading(true)
      try {
        const cached = cacheRef.current[chapterGuid]
        if (cached) {
          const cachedRows = cached.rawText
            ? parseRelationshipText(cached.rawText, { characterLookup })
            : cached.rows
          const nextRows =
            cachedRows.length > 0 ? cachedRows : [createEmptyRelationshipRow()]
          setRows(nextRows)
          cacheRef.current[chapterGuid] = { ...cached, rows: nextRows }
          setIsNonStory(cached.isNonStory)
          setLastUpdatedAt(cached.lastUpdatedAt || null)
          return
        }

        const { data } = await axios.get(
          `${apiBase}/uploadedbooks/book/${bookId}/chapters/${chapterGuid}`
        )
        const relationshipsText: string | null =
          data?.characterRelationships ?? data?.relationships ?? null
        const parsedRows = parseRelationshipText(relationshipsText, { characterLookup })
        const nonStory =
          typeof data?.characterRelationshipsIsNonStory === 'boolean'
            ? data.characterRelationshipsIsNonStory
            : isNonStoryRelationship(relationshipsText || undefined)
        const updatedAt: string | null =
          data?.characterRelationshipsUpdatedAt ??
          data?.characterRelationshipsUpdated ??
          data?.updatedAt ??
          null

        const payload: CachedChapterData = {
          rows: parsedRows.length > 0 ? parsedRows : [createEmptyRelationshipRow()],
          isNonStory: nonStory,
          lastUpdatedAt: updatedAt,
          rawText: relationshipsText,
        }
        cacheRef.current[chapterGuid] = payload
        setRows(payload.rows)
        setIsNonStory(payload.isNonStory)
        setLastUpdatedAt(payload.lastUpdatedAt || null)
      } catch (err) {
        setLoadError(true)
        console.error('Failed to load chapter relationships', err)
        message.error(err instanceof LegacyRelationshipFormatError ? err.message : 'Unable to load chapter relationships.')
        setRows([createEmptyRelationshipRow()])
        setIsNonStory(false)
        setLastUpdatedAt(null)
      } finally {
        setLoading(false)
      }
    },
    [apiBase, bookId, characterLookup]
  )

  React.useEffect(() => {
    if (!visible) {
      return
    }
    const fallbackGuid = orderedChapters[0]?.chapterGuid || chapters[0]?.chapterGuid || null
    const initialGuid = initialChapterGuid || selectedChapterGuid || fallbackGuid
    if (initialGuid && initialGuid !== selectedChapterGuid) {
      setSelectedChapterGuid(initialGuid)
    }
    if (initialGuid) {
      loadChapter(initialGuid)
    }
  }, [visible, initialChapterGuid, orderedChapters, chapters, selectedChapterGuid, loadChapter])

  const handleChapterChange = (chapterGuid: string) => {
    setSelectedChapterGuid(chapterGuid)
    loadChapter(chapterGuid)
  }

  const handleRowChange = (
    rowId: string,
    key: keyof CharacterRelationshipRow,
    value: string
  ) => {
    setRows((prev) =>
      prev.map((row) => {
        if (row.id !== rowId) {
          return row
        }
        if (key === 'characterAName') {
          return { ...row, characterAName: value, characterAId: undefined }
        }
        if (key === 'characterBName') {
          return { ...row, characterBName: value, characterBId: undefined }
        }
        return { ...row, [key]: value }
      })
    )
  }

  const resolveCharacterId = React.useCallback(
    (row: CharacterRelationshipRow, side: 'A' | 'B') => {
      const directId = side === 'A' ? row.characterAId : row.characterBId
      if (directId) {
        return directId
      }
      const name = side === 'A' ? row.characterAName : row.characterBName
      if (!name) {
        return undefined
      }
      return nameToIdLookup[name.toLowerCase()]
    },
    [nameToIdLookup]
  )

  const handleCharacterSelect = (
    rowId: string,
    side: 'A' | 'B',
    selectedId?: string
  ) => {
    setRows((prev) =>
      prev.map((row) => {
        if (row.id !== rowId) {
          return row
        }
        const otherId = resolveCharacterId(row, side === 'A' ? 'B' : 'A')
        if (selectedId && otherId && selectedId === otherId) {
          message.warning('Character A and Character B must be different.')
          return row
        }
        const name = selectedId ? characterLookup[selectedId] || '' : ''
        if (side === 'A') {
          return { ...row, characterAId: selectedId, characterAName: name }
        }
        return { ...row, characterBId: selectedId, characterBName: name }
      })
    )
  }

  const handleAddRow = () => {
    setRows((prev) => [createEmptyRelationshipRow(), ...prev])
  }

  const handleSwapRow = (rowId: string) => {
    setRows((prev) =>
      prev.map((row) =>
        row.id === rowId
          ? {
              ...row,
              characterAName: row.characterBName,
              characterAGender: row.characterBGender,
              characterBName: row.characterAName,
              characterBGender: row.characterAGender,
              characterAId: row.characterBId,
              characterBId: row.characterAId,
              formalityAtoB: row.formalityBtoA ?? row.formality,
              formalityBtoA: row.formalityAtoB ?? row.formality,
              formality: row.formalityBtoA ?? row.formality,
            }
          : row
      )
    )
  }

  const handleDeleteRow = (rowId: string) => {
    setRows((prev) => (prev.length <= 1 ? prev : prev.filter((row) => row.id !== rowId)))
  }

  const sortedRows = React.useMemo(() => {
    if (rowSort === 'none' || rows.length <= 1) {
      return rows
    }
    const getSortKey = (row: CharacterRelationshipRow) => {
      if (rowSort === 'characterA') {
        return (row.characterAName || '').trim().toLowerCase()
      }
      return (row.characterBName || '').trim().toLowerCase()
    }
    return rows
      .map((row, index) => ({ row, index }))
      .sort((a, b) => {
        const keyA = getSortKey(a.row)
        const keyB = getSortKey(b.row)
        const keyCompare = keyA.localeCompare(keyB)
        if (keyCompare !== 0) {
          return keyCompare
        }
        return a.index - b.index
      })
      .map((entry) => entry.row)
  }, [rowSort, rows])

  const preparedRelationshipRows = React.useMemo(() => {
    return rowsToRequestPayload(rows)
      .map((row) => {
        const nameA = row.characterAName?.trim()
        const nameB = row.characterBName?.trim()
        const nameAKey = nameA?.toLowerCase()
        const nameBKey = nameB?.toLowerCase()
        return {
          ...row,
          characterAId: row.characterAId || (nameAKey ? nameToIdLookup[nameAKey] : undefined),
          characterBId: row.characterBId || (nameBKey ? nameToIdLookup[nameBKey] : undefined),
          formalityAtoB: row.formalityAtoB ?? row.formality,
          formalityBtoA: row.formalityBtoA ?? row.formality,
        }
      })
      .filter((row) => row.characterAName && row.characterBName)
  }, [nameToIdLookup, rows])

  const relationshipValidationIssues = React.useMemo(() => {
    if (loading || !selectedChapterGuid) return []
    if (isNonStory) return []
    if (preparedRelationshipRows.length === 0) {
      return ['Add at least one relationship row or mark the chapter as non-story.']
    }
    if (
      preparedRelationshipRows.some(
        (row) =>
          (row.characterAId &&
            row.characterBId &&
            row.characterAId === row.characterBId) ||
          row.characterAName.trim().toLowerCase() === row.characterBName.trim().toLowerCase()
      )
    ) {
      return ['Character A and Character B must be different.']
    }
    if (
      preparedRelationshipRows.some(
        (row) =>
          isCharacterPlaceholderValue(row.characterAName) ||
          isCharacterPlaceholderValue(row.characterBName)
      )
    ) {
      return ['Resolve placeholder character rows before saving.']
    }
    if (
      preparedRelationshipRows.some(
        (row) => row.characterAGender === 'unknown' || row.characterBGender === 'unknown'
      )
    ) {
      return ['Select a Grammar Gender (for translation) form for every relationship row.']
    }
    if (preparedRelationshipRows.some((row) => !row.formalityAtoB || !row.formalityBtoA)) {
      return ['Select Formal / Informal Speech for both directions.']
    }
    return []
  }, [isNonStory, loading, preparedRelationshipRows, selectedChapterGuid])

  React.useEffect(() => {
    if (!Object.keys(nameToIdLookup).length) {
      return
    }
    setRows((prev) => {
      let changed = false
      const next = prev.map((row) => {
        let updated = row
        if (!row.characterAId && row.characterAName) {
          const resolvedA = nameToIdLookup[row.characterAName.toLowerCase()]
          if (resolvedA) {
            updated = {
              ...updated,
              characterAId: resolvedA,
              characterAName: characterLookup[resolvedA] || row.characterAName,
            }
          }
        }
        if (!updated.characterBId && updated.characterBName) {
          const resolvedB = nameToIdLookup[updated.characterBName.toLowerCase()]
          if (resolvedB) {
            updated = {
              ...updated,
              characterBId: resolvedB,
              characterBName: characterLookup[resolvedB] || updated.characterBName,
            }
          }
        }
        if (updated !== row) {
          changed = true
        }
        return updated
      })
      return changed ? next : prev
    })
  }, [characterLookup, nameToIdLookup])

  const handleSave = async () => {
    if (loading || loadError) return
    if (!selectedChapterGuid) return
    if (!userId) {
      message.error('Please sign in to edit relationships.')
      return
    }
    setSaving(true)
    try {
      const payload: any = {
        userId,
        lastKnownUpdatedAt: lastUpdatedAt,
        force: false,
      }
      if (isNonStory) {
        payload.markNonStory = true
      } else {
        payload.relationships = preparedRelationshipRows
      }

      const { data } = await axios.patch(
        `${apiBase}/uploadedbooks/book/${bookId}/chapters/${selectedChapterGuid}/relationships`,
        payload
      )
      const updatedAt =
        data?.metadata?.characterRelationshipsUpdatedAt ||
        data?.metadata?.characterRelationshipsUpdated ||
        new Date().toISOString()
      setLastUpdatedAt(updatedAt)
      cacheRef.current[selectedChapterGuid] = {
        rows: rows.map((row) => ({ ...row })),
        isNonStory,
        lastUpdatedAt: updatedAt,
      }
      message.success('Character relationships updated.')
      if (onSaved) {
        await onSaved()
      }
    } catch (err: any) {
      const conflictMessage =
        err?.response?.status === 409
          ? err.response?.data?.detail?.message ||
            'Chapter relationships changed in the background. Please reload.'
          : null
      message.error(
        conflictMessage || err?.response?.data?.detail || 'Unable to save relationships.'
      )
    } finally {
      setSaving(false)
    }
  }

  const renderRows = () => {
    if (loading) {
      return (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin />
        </div>
      )
    }

    const tableDisabled = isNonStory

    const headerStyles: React.CSSProperties = {
      display: 'grid',
      gridTemplateColumns: GRID_TEMPLATE,
      gap: 8,
      padding: '12px 12px 8px',
      background: token.colorBgContainer,
      borderBottom: `1px solid ${token.colorBorderSecondary}`,
      fontSize: 12,
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      color: token.colorTextSecondary,
      position: 'sticky',
      top: 0,
      zIndex: 2,
    }
    const headerCellLeft: React.CSSProperties = { textAlign: 'left' }
    const headerCellCenter: React.CSSProperties = { textAlign: 'center' }

    const rowWrapperStyles: React.CSSProperties = {
      padding: '12px',
      display: 'grid',
      gridTemplateColumns: GRID_TEMPLATE,
      gap: 8,
      width: '100%',
      alignItems: 'center',
      boxSizing: 'border-box',
    }
    const selectStyles: React.CSSProperties = { width: '100%', minWidth: 0 }
    const actionCellStyles: React.CSSProperties = { justifySelf: 'center', minWidth: 0 }

    return (
      <Space direction="vertical" style={{ width: '100%' }} size="small">
        {tableDisabled && (
          <Alert
            type="info"
            showIcon
            message="Rows ignored for this chapter."
            description="Uncheck non-story to edit relationships."
          />
        )}
        <div className="relationship-matrix-toolbar">
          <Space size={8} align="center">
            <Typography.Text type="secondary">Sort rows</Typography.Text>
            <Select
              value={rowSort}
              onChange={(value) => setRowSort(value)}
              style={{ minWidth: 180 }}
              options={[
                { label: 'Default order', value: 'none' },
                { label: 'Character A', value: 'characterA' },
                { label: 'Character B', value: 'characterB' },
              ]}
              disabled={tableDisabled || rows.length <= 1}
            />
          </Space>
          <Button icon={<PlusOutlined />} onClick={handleAddRow} disabled={tableDisabled}>
            Add relationship
          </Button>
        </div>
        <Card
          size="small"
          className="relationship-matrix-table-card"
          bodyStyle={{ padding: 0 }}
          style={{ opacity: tableDisabled ? 0.6 : 1 }}
        >
          <div className="relationship-matrix-scroll">
            <div style={headerStyles}>
              <span style={headerCellLeft}>Character A</span>
              <span style={headerCellCenter}>Grammar Gender</span>
              <span style={headerCellLeft}>Character B</span>
              <span style={headerCellCenter}>Grammar Gender</span>
              <span style={headerCellCenter}>A -&gt; B speech</span>
              <span style={headerCellCenter}>B -&gt; A speech</span>
              <span style={headerCellCenter}>Actions</span>
            </div>
            <div
              style={{
                overflowX: 'hidden',
                paddingRight: 4,
              }}
            >
              {sortedRows.map((row, index) => (
                <React.Fragment key={row.id}>
                  {index > 0 && <Divider style={{ margin: 0 }} />}
                  <div style={rowWrapperStyles}>
                    {(() => {
                      const selectedAId = resolveCharacterId(row, 'A')
                      const selectedBId = resolveCharacterId(row, 'B')
                      const optionsA = characterOptions.map((option) => ({
                        ...option,
                        disabled: Boolean(selectedBId && option.value === selectedBId),
                      }))
                      const optionsB = characterOptions.map((option) => ({
                        ...option,
                        disabled: Boolean(selectedAId && option.value === selectedAId),
                      }))
                      return (
                        <>
                    <Select
                      showSearch
                      allowClear
                      placeholder="Select character"
                      value={selectedAId}
                      onChange={(value) => handleCharacterSelect(row.id, 'A', value)}
                      style={selectStyles}
                      disabled={tableDisabled}
                      options={optionsA}
                      filterOption={(input, option) => {
                        const label = (option?.label || '').toString().toLowerCase()
                        const keywords = (option as any)?.keywords || ''
                        const query = input.toLowerCase()
                        return label.includes(query) || keywords.includes(query)
                      }}
                      notFoundContent={
                        characterOptions.length === 0 ? 'No characters available' : undefined
                      }
                    />
                    <Select
                      value={row.characterAGender === 'unknown' ? undefined : row.characterAGender}
                      onChange={(value) => handleRowChange(row.id, 'characterAGender', value)}
                      style={selectStyles}
                      placeholder="Select"
                      disabled={tableDisabled}
                      options={SELECTABLE_RELATIONSHIP_GENDERS.map((gender) => ({
                        label: formatRelationshipGenderLabel(gender),
                        value: gender,
                      }))}
                    />
                    <Select
                      showSearch
                      allowClear
                      placeholder="Select character"
                      value={selectedBId}
                      onChange={(value) => handleCharacterSelect(row.id, 'B', value)}
                      style={selectStyles}
                      disabled={tableDisabled}
                      options={optionsB}
                      filterOption={(input, option) => {
                        const label = (option?.label || '').toString().toLowerCase()
                        const keywords = (option as any)?.keywords || ''
                        const query = input.toLowerCase()
                        return label.includes(query) || keywords.includes(query)
                      }}
                      notFoundContent={
                        characterOptions.length === 0 ? 'No characters available' : undefined
                      }
                    />
                    <Select
                      value={row.characterBGender === 'unknown' ? undefined : row.characterBGender}
                      onChange={(value) => handleRowChange(row.id, 'characterBGender', value)}
                      style={selectStyles}
                      placeholder="Select"
                      disabled={tableDisabled}
                      options={SELECTABLE_RELATIONSHIP_GENDERS.map((gender) => ({
                        label: formatRelationshipGenderLabel(gender),
                        value: gender,
                      }))}
                    />
                    <Select
                      value={row.formalityAtoB ?? row.formality}
                      onChange={(value) => handleRowChange(row.id, 'formalityAtoB', value)}
                      style={selectStyles}
                      disabled={tableDisabled}
                      options={RELATIONSHIP_FORMALITIES.map((formality) => ({
                        label: formality,
                        value: formality,
                      }))}
                    />
                    <Select
                      value={row.formalityBtoA ?? row.formality}
                      onChange={(value) => handleRowChange(row.id, 'formalityBtoA', value)}
                      style={selectStyles}
                      disabled={tableDisabled}
                      options={RELATIONSHIP_FORMALITIES.map((formality) => ({
                        label: formality,
                        value: formality,
                      }))}
                    />
                    <Space size={4} style={actionCellStyles}>
                      <Button
                        icon={<SwapOutlined />}
                        size="small"
                        onClick={() => handleSwapRow(row.id)}
                        disabled={tableDisabled}
                      title="Swap A/B"
                    />
                      <Button
                        icon={<MinusCircleOutlined />}
                        danger
                        size="small"
                        onClick={() => handleDeleteRow(row.id)}
                        disabled={tableDisabled || rows.length <= 1}
                      />
                    </Space>
                        </>
                      )
                    })()}
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
        </Card>
      </Space>
    )
  }

  const tourReady = !loading
  const selectedChapter = orderedChapters.find((chapter) => chapter.chapterGuid === selectedChapterGuid)
  const footerSummary = selectedChapter
    ? `Chapter ${selectedChapter.chapterNumber}: ${selectedChapter.chapterName} - ${isNonStory ? 'non-story' : `${rows.length} relationship row${rows.length === 1 ? '' : 's'}`}`
    : 'Select a chapter to edit relationship rows.'
  const footerStatus = relationshipValidationIssues.length
    ? 'Requirements needed'
    : saving
      ? 'Saving relationships'
      : 'Ready to save'

  return (
    <Modal
      title={
        <div className="translation-modal-titlebar">
          <span className="translation-modal-titlebar__title">Edit Character Relationships</span>
        </div>
      }
      visible={visible}
      onCancel={handleClose}
      width={1000}
      style={{ top: 40, maxWidth: '92vw' }}
      wrapClassName="characters-modal"
      className="relationship-matrix-modal"

      footer={
        <div className="relationship-matrix-footer">
          <div className="relationship-matrix-footer__summary" aria-live="polite">
            <strong className={relationshipValidationIssues.length ? 'is-needed' : 'is-ready'}>{footerStatus}</strong>
            <span>{footerSummary}</span>
          </div>
          <div className="relationship-matrix-footer__actions">
          <Button key="cancel" onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
            <Button
              key="analyze"
              disabled={analyzeDisabled || saving}
              onClick={() => {
                handleClose()
                onOpenAnalysis()
              }}
            >
              Run analysis
            </Button>
            <Button
              key="save"
              type="primary"
              className="app-primary-cta"
              loading={saving}
              disabled={loading || loadError || relationshipValidationIssues.length > 0}
              onClick={handleSave}
            >
              Save changes
            </Button>
          </div>
        </div>
      }
    >
      <div className="relationship-matrix-body" data-tour-id="relationship-matrix-modal" data-testid="tutorial:characters:relationship-rules-modal" data-tour-ready={tourReady ? 'true' : 'false'}>
        <section
          className="relationship-matrix-block"
          data-tour-id="relationship-matrix-chapter-section"
          data-testid="tutorial:characters:relationship-rules-chapter"
          data-tour-ready={tourReady ? 'true' : 'false'}
        >
          <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Review or tweak relationship rules chapter by chapter. A -&gt; B speech controls how
            Character A addresses Character B, and B -&gt; A speech controls the reverse. Grammar
            Gender (for translation) helps languages that require it and should match the form used
            in the chapter text.
          </Typography.Paragraph>
        </section>
        {relationshipValidationIssues.length > 0 && (
          <section className="relationship-matrix-block relationship-matrix-block--attention">
            <Alert
              type="warning"
              showIcon
              message="Relationship requirements"
              description={relationshipValidationIssues[0]}
            />
          </section>
        )}
        <section className="relationship-matrix-block">
          <label style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>Select chapter</label>
          <Select
            showSearch
            value={selectedChapterGuid || undefined}
            onChange={handleChapterChange}
            style={{ width: '100%' }}
            filterOption={(input, option) =>
              (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
            }
            options={orderedChapters.map((chapter) => ({
              label: `Chapter ${chapter.chapterNumber}: ${chapter.chapterName}`,
              value: chapter.chapterGuid,
            }))}
          />
        </section>
        <section className="relationship-matrix-block">
        <Checkbox
          checked={isNonStory}
          onChange={(e) => setIsNonStory(e.target.checked)}
        >
          This chapter is non-story content (skip relationship analysis)
        </Checkbox>
        </section>
        <section className="relationship-matrix-block relationship-matrix-block--rows" data-testid="tutorial:characters:relationship-rules-rows">
        {renderRows()}
        </section>
      </div>
    </Modal>
  )
}

export default RelationshipMatrixEditorModal
